/**
 * Guards against the React "NotFoundError: Failed to execute 'removeChild' (or 'insertBefore')
 * on 'Node': The node to be removed is not a child of this node." crash caused by in-browser
 * page translation (Edge/Chrome "Translate this page", Google Translate extension, ...).
 *
 * WHY THIS IS NEEDED:
 * Browser translators mutate the DOM directly: they replace the original text nodes with
 * translated ones. React, however, still holds references to the original text nodes in its
 * fiber tree. When React later reconciles and tries to remove or move one of those nodes, the
 * node is no longer a child of the parent React remembers, so the native removeChild() /
 * insertBefore() call throws a DOMException (NotFoundError). That exception escapes to the
 * React Router ErrorBoundary and crashes the whole page for the user. See facebook/react#11538.
 *
 * THIS GUARD:
 * Wraps Node.prototype.removeChild and Node.prototype.insertBefore so that a call targeting a
 * node whose parent is not the expected one becomes a no-op returning the node, instead of
 * throwing. This lets React's commit phase finish and recover on the next render, keeping the
 * (translator-mutated) DOM intact. It intentionally stays silent: the mismatch is an expected
 * artifact of third-party translation, not a bug in our code, so logging it would only add
 * Sentry/console noise — which is exactly what this fix removes.
 */
export const initTranslationCrashGuard = (): void => {
    if (typeof Node !== 'function' || !Node.prototype) return

    const originalRemoveChild = Node.prototype.removeChild
    Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
        if (child.parentNode !== this) {
            return child
        }
        return originalRemoveChild.call(this, child) as T
    }

    const originalInsertBefore = Node.prototype.insertBefore
    Node.prototype.insertBefore = function <T extends Node>(this: Node, newNode: T, referenceNode: Node | null): T {
        if (referenceNode && referenceNode.parentNode !== this) {
            return newNode
        }
        return originalInsertBefore.call(this, newNode, referenceNode) as T
    }
}
